import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  FolderOpen,
  Home,
  LifeBuoy,
  Loader2,
  Menu,
  PackageCheck,
  Send,
  Settings as SettingsIcon,
  Sparkles,
  Wallet,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { API, fetchDashboard, formatApiErrorDetail, inputCls } from "./shared";

const NAV = [
  {
    section: "Workspace",
    items: [
      { to: "/dashboard", label: "Overview", icon: Home, test: "sidebar-overview-link", end: true },
      { to: "/dashboard/new", label: "New Brief", icon: Send, test: "sidebar-new-brief-link", highlight: true },
      { to: "/dashboard/projects", label: "My Projects", icon: FolderOpen, test: "sidebar-projects-link" },
      { to: "/dashboard/deliveries", label: "Deliveries", icon: PackageCheck, test: "sidebar-deliveries-link" },
    ],
  },
  {
    section: "Account",
    items: [
      { to: "/dashboard/credits", label: "Credits and Plan", icon: Wallet, test: "sidebar-credits-link" },
      { to: "/dashboard/help", label: "Help and Support", icon: LifeBuoy, test: "sidebar-help-link" },
      { to: "/dashboard/settings", label: "Settings", icon: SettingsIcon, test: "sidebar-settings-link" },
    ],
  },
];

function SidebarNav({ onNavigate, variant = "desktop" }) {
  const tid = (t) => (variant === "mobile" ? `mobile-${t}` : t);
  return (
    <nav data-testid={tid("sidebar-nav")} className="flex h-full flex-col">
      {variant === "desktop" && (
        <Link to="/dashboard" data-testid="sidebar-logo" className="flex items-center gap-2 px-5 pb-6 pt-6" onClick={onNavigate}>
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-amber-400">
            <Sparkles className="h-4 w-4 text-[#1A1204]" />
          </span>
          <span className="text-lg font-extrabold tracking-tight text-white">GRAPHO</span>
        </Link>
      )}
      {NAV.map((group) => (
        <div key={group.section} className="px-3 pb-2">
          <p className="px-2 pb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-600">
            {group.section}
          </p>
          {group.items.map(({ to, label, icon: Icon, test, end, highlight }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={tid(test)}
              onClick={onNavigate}
              className={({ isActive }) =>
                `mb-1 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${
                  highlight
                    ? "bg-amber-400 text-[#1A1204] shadow-[0_0_24px_rgba(245,158,11,0.35)] hover:bg-amber-300"
                    : isActive
                      ? "border border-amber-400/40 bg-amber-400/10 text-amber-300"
                      : "text-slate-400 hover:bg-white/5 hover:text-slate-100"
                }`
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              {label}
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  );
}

export default function DashboardLayout() {
  const params = useSearchParams()[0];
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [passwordlessEmail, setPasswordlessEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [error, setError] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: 1024px)").matches : true
  );

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const load = async (em) => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
      setError("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const d = await fetchDashboard(em);
      setData(d);
      localStorage.setItem("grapho_customer_email", em);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not load your dashboard.");
      setData(null);
    }
    setLoading(false);
  };

  const verifyToken = async (token) => {
    setSigningIn(true);
    setError("");
    try {
      const { data: d } = await axios.post(`${API}/auth/verify`, { token });
      setData(d);
      setPasswordlessEmail(d.customer.email);
      localStorage.setItem("grapho_customer_email", d.customer.email);
      toast.success("Signed in — welcome back.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "This sign-in link is invalid or has expired.");
    }
    setSigningIn(false);
  };

  const sendMagicLink = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(passwordlessEmail.trim())) {
      setError("Please enter a valid email address.");
      return;
    }
    setError("");
    try {
      await axios.post(`${API}/auth/magic-link`, { email: passwordlessEmail.trim() });
      setMagicSent(true);
      toast.success("Sign-in link sent — check your inbox.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not send the sign-in link.");
    }
  };

  useEffect(() => {
    const token = params.get("token");
    if (token) verifyToken(token);
    else if (params.get("email")) load(params.get("email"));
    else {
      const saved = localStorage.getItem("grapho_customer_email");
      if (saved) {
        setEmail(saved);
        load(saved);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close the mobile menu whenever the route changes.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const refresh = () => {
    const em = data?.customer?.email || email || localStorage.getItem("grapho_customer_email");
    if (em) return load(em);
    return Promise.resolve();
  };

  const ctx = {
    customer: data?.customer || null,
    briefs: data?.briefs || [],
    email: data?.customer?.email || email,
    refresh,
  };

  return (
    <div className="min-h-screen bg-[#070B14]">
      {/* Desktop sidebar */}
      {isDesktop && (
        <aside className="fixed inset-y-0 left-0 z-40 w-60 border-r border-white/5 bg-[#0A1020]/90 backdrop-blur-xl">
          <SidebarNav />
        </aside>
      )}

      {/* Mobile top bar + hamburger */}
      <div className="fixed inset-x-0 top-0 z-40 flex items-center justify-between border-b border-white/5 bg-[#0A1020]/90 px-4 py-3 backdrop-blur-xl lg:hidden">
        <Link to="/dashboard" data-testid="mobile-logo" className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-amber-400">
            <Sparkles className="h-4 w-4 text-[#1A1204]" />
          </span>
          <span className="text-lg font-extrabold tracking-tight text-white">GRAPHO</span>
        </Link>
        <button
          data-testid="mobile-menu-button"
          aria-label="Toggle menu"
          onClick={() => setMenuOpen((v) => !v)}
          className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-slate-200 transition-colors hover:border-white/25"
        >
          {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {menuOpen && (
        <div className="fixed inset-0 z-30 lg:hidden" onClick={() => setMenuOpen(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className="absolute inset-y-0 left-0 w-72 border-r border-white/10 bg-[#0A1020] pt-10"
            onClick={(e) => e.stopPropagation()}
          >
            <SidebarNav onNavigate={() => setMenuOpen(false)} variant="mobile" />
          </div>
        </div>
      )}

      {/* Content */}
      <div className="lg:pl-60">
        <main className="mx-auto max-w-5xl px-5 pb-24 pt-20 sm:px-8 lg:pt-10">
          {!data ? (
            <div className="glass mx-auto mt-10 max-w-md rounded-3xl p-8 text-center">
              <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.28em] text-sky-400">
                Grapho Studio
              </p>
              <h1 className="mt-4 text-2xl font-bold tracking-tight text-white">Open your studio</h1>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">
                Enter the email you used at checkout to see your credits and orders.
              </p>
              <input
                data-testid="dashboard-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seller@yourshop.com"
                className={`${inputCls} mt-6`}
              />
              <button
                data-testid="dashboard-open-button"
                onClick={() => load(email.trim())}
                disabled={loading || signingIn}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-amber-400 px-6 py-3.5 text-sm font-bold text-[#1A1204] transition-all duration-300 hover:bg-amber-300 disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {loading ? "Opening..." : "Open My Studio"}
              </button>
              {signingIn ? (
                <p
                  data-testid="dashboard-signing-in"
                  className="mt-4 inline-flex w-full items-center justify-center gap-2 text-sm font-semibold text-sky-300"
                >
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Signing you in...
                </p>
              ) : (
                <button
                  data-testid="dashboard-magic-link-button"
                  onClick={sendMagicLink}
                  className="mt-3 w-full text-sm font-semibold text-slate-400 transition-colors hover:text-sky-300"
                >
                  {magicSent ? "Resend sign-in link" : "Email me a sign-in link instead"}
                </button>
              )}
              {error && (
                <p data-testid="dashboard-error" className="mt-4 text-sm text-rose-400">
                  {error}
                </p>
              )}
            </div>
          ) : (
            <Outlet context={ctx} />
          )}
        </main>
      </div>
    </div>
  );
}
